/**
 * VerifyPage — magic-link landing (route `/verify?token=…`).
 *
 * Story: epic-uat-portal-001-8.
 *
 * The target of the sign-in link emailed by `/request-link`. It does NOT auto-
 * verify on load: a magic link that signed the tester in just by being opened is
 * a login-CSRF / session-fixation vector (an attacker could email their own link
 * and land the victim in the attacker's account, and any link preview / scanner
 * would silently burn + consume the token). Instead it shows an explicit "Sign in"
 * confirmation; verification only happens on the tester's click.
 *   (The stronger fix — binding the token to the requesting browser via an
 *   HttpOnly nonce cookie set at /request-link and required at /verify-link — is
 *   a backend hardening follow-up; this is the frontend mitigation.)
 *
 * On success it opens a session and routes to a same-origin returnUrl (origin-
 * validated against open-redirect), else `/app`.
 */

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTesterAuth } from '@/auth/testerAuthContext';
import { verifyLink, TesterAccountApiError } from '@/services/testerAccountService';
import { safeReturnUrl } from './returnUrl';

type VerifyState = 'confirm' | 'verifying' | 'error';

export function VerifyPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const returnUrl = safeReturnUrl(searchParams.get('returnUrl'));
  const navigate = useNavigate();
  const { login } = useTesterAuth();

  const [state, setState] = useState<VerifyState>(token ? 'confirm' : 'error');
  const [error, setError] = useState<string | null>(
    token ? null : 'This sign-in link is missing its token. Request a new one.',
  );

  async function handleSignIn(): Promise<void> {
    setState('verifying');
    try {
      const session = await verifyLink(token);
      login(session.account, session.sessionToken);
      navigate(returnUrl, { replace: true });
    } catch (err) {
      setError(
        err instanceof TesterAccountApiError
          ? err.message
          : 'Could not verify your sign-in link. Please try again.',
      );
      setState('error');
    }
  }

  if (state === 'error') {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-8 text-center">
        <h1 className="text-2xl font-bold" data-testid="verify-error-heading">
          Sign-in link problem
        </h1>
        <p className="text-sm text-red-600" role="alert" data-testid="verify-error">
          {error}
        </p>
        <Link to="/login" className="underline" data-testid="verify-request-new">
          Request a new sign-in link
        </Link>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-2xl font-bold" data-testid="verify-heading">
        Sign in to the Tester Portal
      </h1>
      <p className="text-sm text-muted-foreground" data-testid="verify-message">
        Click below to finish signing in with your secure link.
      </p>
      <button
        type="button"
        onClick={() => void handleSignIn()}
        disabled={state === 'verifying'}
        className="rounded bg-emerald-700 px-4 py-2 font-semibold text-white disabled:opacity-60"
        data-testid="verify-signin"
      >
        {state === 'verifying' ? 'Signing you in…' : 'Sign in'}
      </button>
    </main>
  );
}
